import { useAppDispatch, useAppSelector } from '@/app/hooks';
import DeleteSelectedBtn from '@/components/MantineFileManager/btn/DeleteSelectedBtn';
import type { FileManagerItem } from '@/components/MantineFileManager/file.type';
import { useGetFilesQuery } from '@/features/api/endpoints/fileEndpoints';
import { useGetFoldersQuery } from '@/features/api/endpoints/folderEndpoints';
import { clearSelection, setCurrentFolderId } from '@/features/drive/driveSlice';
import { findFolderPath } from '@/shared/utils/file.utils';
import { Divider, Group, Loader, Paper, Stack } from '@mantine/core';
import { useTranslate } from '@tolgee/react';
import { useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import CreateFolderBtn from './btn/CreateFolderBtn';
import UploadFileBtn from './btn/UploadFileBtn';
import FileDataTable from './FileDataTable';
import FileManagerBreadcrumb from './FileManagerBreadcrumb';

type Props = {
  rootFolderId: string;
  currentFolderIdFromUrl?: string;
};

const DRIVE_ROOT_PATH = '/drive';

const MantineFileManager = ({ rootFolderId, currentFolderIdFromUrl }: Props) => {
  const { t } = useTranslate();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const currentFolderId = useAppSelector(state => state.drive.currentFolderId);
  const selectedItemIds = useAppSelector(state => state.drive.selectedItemIds);
  const folderIdFromUrl = currentFolderIdFromUrl ?? rootFolderId;
  const effectiveCurrentFolderId = currentFolderId ?? folderIdFromUrl;

  useEffect(() => {
    if (currentFolderId === folderIdFromUrl) {
      return;
    }

    dispatch(setCurrentFolderId(folderIdFromUrl));
    dispatch(clearSelection());
  }, [currentFolderId, dispatch, folderIdFromUrl]);

  const { data: folders = [], isFetching: areFoldersFetching } = useGetFoldersQuery(undefined);
  const { data: files = [], isFetching: areFilesFetching } = useGetFilesQuery(undefined);

  const rootFolder = useMemo(
    () => folders.find(folder => folder.id === rootFolderId) ?? null,
    [folders, rootFolderId],
  );

  const currentFolderPath = useMemo(
    () => findFolderPath(folders, effectiveCurrentFolderId),
    [effectiveCurrentFolderId, folders],
  );

  const isCurrentFolderInRootSubtree = useMemo(
    () => currentFolderPath.some(folder => folder.id === rootFolderId),
    [currentFolderPath, rootFolderId],
  );

  useEffect(() => {
    if (!effectiveCurrentFolderId || areFoldersFetching) {
      return;
    }

    const folderExists =
      effectiveCurrentFolderId === rootFolderId ||
      folders.some(folder => folder.id === effectiveCurrentFolderId);

    if (!folderExists || !isCurrentFolderInRootSubtree) {
      navigate(DRIVE_ROOT_PATH, { replace: true });
    }
  }, [
    areFoldersFetching,
    effectiveCurrentFolderId,
    folders,
    isCurrentFolderInRootSubtree,
    navigate,
    rootFolderId,
  ]);

  const navigateToFolder = (folderId: string) => {
    if (folderId === folderIdFromUrl) {
      dispatch(clearSelection());
      return;
    }

    const targetPath =
      folderId === rootFolderId ? DRIVE_ROOT_PATH : `${DRIVE_ROOT_PATH}/${folderId}`;
    navigate(targetPath);
  };

  const breadcrumbFolders = useMemo(
    () =>
      currentFolderPath
        .slice(currentFolderPath.findIndex(folder => folder.id === rootFolderId) + 1)
        .filter(folder => folder.id !== rootFolderId),
    [currentFolderPath, rootFolderId],
  );

  const selectedItems = useMemo(
    () =>
      selectedItemIds
        .map<FileManagerItem | null>((id: string) => {
          const folder = folders.find(currentFolder => currentFolder.id === id);
          if (folder) {
            return {
              data: folder,
              id: folder.id,
              kind: 'folder',
            };
          }

          const file = files.find(currentFile => currentFile.id === id);
          if (file) {
            return {
              data: file,
              id: file.id,
              kind: 'file',
            };
          }

          return null;
        })
        .filter((item: FileManagerItem | null): item is FileManagerItem => item !== null),
    [files, folders, selectedItemIds],
  );

  const isBusy = areFoldersFetching || areFilesFetching;

  return (
    <Paper withBorder p="md" radius="md">
      <Stack gap="md">
        <Group justify="space-between" align="center" wrap="wrap">
          <FileManagerBreadcrumb
            currentFolderId={effectiveCurrentFolderId}
            breadcrumbFolders={breadcrumbFolders}
            rootFolderId={rootFolderId}
            rootLabel={rootFolder?.name ?? t('drive.root')}
            onNavigateToFolder={navigateToFolder}
          />

          <Group gap="xs" wrap="wrap">
            <CreateFolderBtn />
            <UploadFileBtn />
            <DeleteSelectedBtn selectedItems={selectedItems} />
          </Group>
        </Group>
        <Divider />
        {isBusy ? (
          <Group justify="center" py="xl">
            <Loader />
          </Group>
        ) : (
          <>
            {effectiveCurrentFolderId && (
              <FileDataTable
                folders={folders}
                files={files}
                currentFolderId={effectiveCurrentFolderId}
                selectedItemIds={selectedItemIds}
                onOpenFolder={navigateToFolder}
              />
            )}
          </>
        )}
      </Stack>
    </Paper>
  );
};

export default MantineFileManager;
